import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Close,
  ChevronLeft,
  ChevronRight,
  Person,
  Email,
  Phone,
  Cake,
  Work,
  LocationOn,
  Favorite,
  PhotoLibrary,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Update,
  FavoriteRounded,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  UserStatus,
  ReviewStatus,
  SilverConnectAdminUserDetailDto,
  AdminApiResponse,
  UpdateUserStatusRequest,
} from '../../types';
import { apiClient, API_ENDPOINTS } from '../../config/api';

// ─── Design tokens ─────────────────────────────────────────────────
const PINK  = '#EC4899';
const CORAL = '#FF6B35';

// ─── 거절 사유 코드 목록 ────────────────────────────────────────────
const REJECT_REASON_CODES: { code: string; label: string }[] = [
  { code: 'PHOTO_BLUR',          label: '프로필 사진이 흐리거나 얼굴이 잘 보이지 않음' },
  { code: 'PHOTO_MISSING',       label: '프로필 사진 부족 (5장 미만)' },
  { code: 'PHOTO_INAPPROPRIATE', label: '부적절한 사진 포함' },
  { code: 'INFO_INCOMPLETE',     label: '필수 정보 미입력 (직업·거주지·자기소개)' },
  { code: 'INFO_INCONSISTENT',   label: '입력 정보 불일치' },
  { code: 'AGE_OUT_OF_RANGE',    label: '가입 연령 기준 미충족 (50~70세)' },
  { code: 'DUPLICATE',           label: '중복 계정 확인' },
];

// ─── Status config ─────────────────────────────────────────────────
const REVIEW_CONFIG: Record<ReviewStatus, { label: string; color: string; bg: string; dot: string; icon: React.ReactNode }> = {
  [ReviewStatus.APPROVED]:  { label: '승인됨', color: '#2E7D32', bg: '#E8F5E9', dot: '#43A047', icon: <CheckCircle  sx={{ fontSize: 14 }} /> },
  [ReviewStatus.SUBMITTED]: { label: '제출됨', color: '#E65100', bg: '#FFF3E0', dot: '#FF6D00', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  [ReviewStatus.UPDATED]:   { label: '수정됨', color: '#1565C0', bg: '#E3F2FD', dot: '#1E88E5', icon: <Update       sx={{ fontSize: 14 }} /> },
  [ReviewStatus.REJECTED]:  { label: '거부됨', color: '#C62828', bg: '#FFEBEE', dot: '#E53935', icon: <Cancel       sx={{ fontSize: 14 }} /> },
};

const USER_STATUS_MAP: Partial<Record<UserStatus, { label: string; color: string }>> = {
  [UserStatus.APPROVED]: { label: '활성',   color: '#2E7D32' },
  [UserStatus.PENDING]:  { label: '대기',   color: '#E65100' },
  [UserStatus.REJECTED]: { label: '거부',   color: '#C62828' },
  [UserStatus.GUEST]:    { label: '게스트', color: '#9E9E9E' },
};

// ─── Helpers ───────────────────────────────────────────────────────
const formatDate = (d: string | null) => {
  if (!d) return '–';
  return new Date(d).toLocaleString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const calculateAge = (birthDate: string | null) => {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() - birth.getMonth() < 0 ||
      (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return age;
};

const getPreferredAgeText = (v: string | null) => {
  if (!v) return null;
  return v === 'YOUNGER' ? '연하 선호' : v === 'OLDER' ? '연상 선호' : '나이 무관';
};

// ─── Sub-components ────────────────────────────────────────────────

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}
const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, borderBottom: '1px solid #F3EBF8', '&:last-child': { borderBottom: 'none' } }}>
    <Box sx={{ color: PINK, mt: 0.2, flexShrink: 0 }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.9rem', color: '#333', fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

interface SectionCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  accent?: string;
}
const SectionCard: React.FC<SectionCardProps> = ({ title, icon, children, accent = PINK }) => (
  <Card elevation={0} sx={{
    border: '1px solid #F0E8F5',
    borderLeft: `4px solid ${accent}`,
    borderRadius: '16px',
    overflow: 'hidden',
  }}>
    <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F3EBF8', display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box sx={{ color: accent }}>{icon}</Box>
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#1A0A2E' }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 3 }}>
      {children}
    </Box>
  </Card>
);

// ─── Main component ────────────────────────────────────────────────
const AdminUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [userDetail, setUserDetail]       = useState<SilverConnectAdminUserDetailDto | null>(null);
  const [loading, setLoading]             = useState(true);
  const [statusDialog, setStatusDialog]   = useState(false);
  const [newStatus, setNewStatus]             = useState<ReviewStatus>(ReviewStatus.SUBMITTED);
  const [statusReason, setStatusReason]       = useState('');
  const [selectedRejectCodes, setSelectedRejectCodes] = useState<string[]>([]);
  const [lightboxOpen, setLightboxOpen]   = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  useEffect(() => {
    if (userId) loadUserDetail();
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserDetail = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data: AdminApiResponse<SilverConnectAdminUserDetailDto> =
        await apiClient.get(`/api-admin/v1/gth-common/silver-connect/members/${userId}`);
      if (data.resultType === 'SUCCESS' && data.result) setUserDetail(data.result);
    } catch (error) {
      console.error('사용자 정보 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    if (!userDetail) return;
    try {
      const request: UpdateUserStatusRequest = {
        reviewStatus: newStatus,
        rejectReason: newStatus === ReviewStatus.REJECTED ? statusReason || undefined : undefined,
        rejectReasonCodes: newStatus === ReviewStatus.REJECTED ? selectedRejectCodes : undefined,
      };
      const response: AdminApiResponse<SilverConnectAdminUserDetailDto> =
        await apiClient.put(API_ENDPOINTS.ADMIN.UPDATE_USER_STATUS(userDetail.userId), request);
      if (response.resultType === 'SUCCESS' && response.result) {
        setUserDetail(response.result);
        setStatusDialog(false);
        setStatusReason('');
      }
    } catch (error) {
      console.error('상태 변경 실패:', error);
    }
  };

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  if (loading || !userDetail) {
    return (
      <AdminLayout>
        <Box sx={{ background: '#F8F4F9', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
          <CircularProgress sx={{ color: PINK }} size={48} />
          <Typography sx={{ color: '#888', fontSize: '0.9rem' }}>회원 정보를 불러오는 중...</Typography>
        </Box>
      </AdminLayout>
    );
  }

  const reviewCfg    = REVIEW_CONFIG[userDetail.reviewStatus];
  const userCfg      = userDetail.userStatus ? USER_STATUS_MAP[userDetail.userStatus] : null;
  const age          = calculateAge(userDetail.birthDate);
  const sortedPhotos = userDetail.profilePhotos
    ? [...userDetail.profilePhotos].sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  return (
    <AdminLayout>
      <style>{`
        @keyframes profileIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <Box sx={{ background: '#F8F4F9', minHeight: '100vh' }}>

        {/* ── Sticky nav bar ── */}
        <Box sx={{
          background: '#fff',
          borderBottom: '1px solid #F0E8F5',
          px: { xs: 2, md: 4 }, py: 1.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/admin/users')}
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 600,
                color: '#666', border: '1px solid #EEE', px: 1.5,
                '&:hover': { borderColor: PINK, color: PINK, background: `${PINK}08` },
              }}
            >
              목록으로
            </Button>
            <Typography sx={{ color: '#CCC' }}>·</Typography>
            <Typography sx={{ fontWeight: 700, color: '#1A0A2E', fontSize: '0.95rem' }}>
              {userDetail.name}
            </Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              background: reviewCfg.bg, color: reviewCfg.color,
              px: 1.2, py: 0.3, borderRadius: '100px', fontSize: '0.72rem', fontWeight: 700,
            }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: reviewCfg.dot }} />
              {reviewCfg.label}
            </Box>
          </Box>

          <Button
            variant="contained"
            size="small"
            startIcon={<Edit sx={{ fontSize: 16 }} />}
            onClick={() => { setNewStatus(userDetail.reviewStatus); setStatusDialog(true); }}
            sx={{
              borderRadius: '10px', textTransform: 'none', fontWeight: 700,
              background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
              boxShadow: `0 4px 14px ${PINK}44`,
              '&:hover': { background: `linear-gradient(135deg, #D63384, #E55A28)` },
            }}
          >
            심사 상태 변경
          </Button>
        </Box>

        {/* ── Page body ── */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 } }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '300px 1fr' },
            gap: 3, alignItems: 'start',
          }}>

            {/* ── Left sidebar ── */}
            <Box sx={{ position: { xs: 'static', md: 'sticky' }, top: 72, animation: 'profileIn 0.5s ease both' }}>
              <Card elevation={0} sx={{ border: '1px solid #F0E8F5', borderRadius: '20px', overflow: 'hidden' }}>

                {/* Profile hero */}
                <Box sx={{
                  background: `linear-gradient(135deg, #1A0A2E 0%, ${PINK} 60%, ${CORAL} 100%)`,
                  p: 3, textAlign: 'center', position: 'relative',
                }}>
                  <Box sx={{ position: 'absolute', top: 12, right: 16, fontSize: 28, opacity: 0.15, color: '#fff', userSelect: 'none' }}>♥</Box>
                  <Box sx={{ position: 'absolute', bottom: 16, left: 12, fontSize: 20, opacity: 0.1, color: '#fff', userSelect: 'none' }}>✦</Box>

                  <Avatar
                    src={userDetail.profileImageUrl || undefined}
                    sx={{
                      width: 88, height: 88, mx: 'auto', mb: 2,
                      background: '#4A1265', fontSize: '2rem', fontWeight: 800,
                      border: '4px solid rgba(255,255,255,0.9)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    }}
                  >
                    {userDetail.name?.charAt(0)}
                  </Avatar>

                  <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem', mb: 0.5 }}>
                    {userDetail.name}
                    {age != null && (
                      <Typography component="span" sx={{ fontWeight: 400, fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', ml: 0.8 }}>
                        {age}세
                      </Typography>
                    )}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.8rem', mb: 2 }}>
                    {userDetail.email}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.6,
                      background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)',
                      border: '1px solid rgba(255,255,255,0.25)',
                      color: '#fff', px: 1.5, py: 0.5, borderRadius: '100px',
                      fontSize: '0.72rem', fontWeight: 700,
                    }}>
                      {reviewCfg.icon} {reviewCfg.label}
                    </Box>
                    {userCfg && (
                      <Box sx={{
                        display: 'inline-flex', alignItems: 'center',
                        background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(4px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: 'rgba(255,255,255,0.85)', px: 1.5, py: 0.5, borderRadius: '100px',
                        fontSize: '0.72rem', fontWeight: 600,
                      }}>
                        {userCfg.label}
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Info rows */}
                <Box sx={{ p: 2.5 }}>
                  <InfoRow icon={<Person sx={{ fontSize: 18 }} />} label="사용자 ID" value={`#${userDetail.userId}`} />
                  {userDetail.gender && (
                    <InfoRow icon={<FavoriteRounded sx={{ fontSize: 18 }} />} label="성별"
                      value={userDetail.gender === 'MALE' ? '남성 ♂' : '여성 ♀'} />
                  )}
                  {userDetail.birthDate && (
                    <InfoRow icon={<Cake sx={{ fontSize: 18 }} />} label="생년월일" value={userDetail.birthDate} />
                  )}
                  {userDetail.residenceRegion && (
                    <InfoRow icon={<LocationOn sx={{ fontSize: 18 }} />} label="거주지역"
                      value={`${userDetail.residenceRegion.level1Name} ${userDetail.residenceRegion.level2Name}`} />
                  )}
                  {userDetail.jobTitle && (
                    <InfoRow icon={<Work sx={{ fontSize: 18 }} />} label="직업" value={userDetail.jobTitle} />
                  )}
                  {userDetail.phoneNumber && (
                    <InfoRow icon={<Phone sx={{ fontSize: 18 }} />} label="연락처" value={userDetail.phoneNumber} />
                  )}
                  <InfoRow icon={<Email sx={{ fontSize: 18 }} />} label="가입일" value={formatDate(userDetail.joinedTime)} />
                  <InfoRow icon={<Update sx={{ fontSize: 18 }} />} label="최근 로그인" value={formatDate(userDetail.lastLoginTime)} />
                  {userDetail.resubmissionCount > 0 && (
                    <InfoRow
                      icon={<Update sx={{ fontSize: 18 }} />}
                      label="심사 요청"
                      value={`${userDetail.resubmissionCount + 1}번째 심사 (재심사 ${userDetail.resubmissionCount}회)`}
                    />
                  )}
                </Box>
              </Card>
            </Box>

            {/* ── Right detail sections ── */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

              {/* 매칭 선호도 */}
              {(userDetail.preferredAge ||
                (userDetail.preferredRegions && userDetail.preferredRegions.length > 0) ||
                (userDetail.interests && userDetail.interests.length > 0)) && (
                <Box sx={{ animation: 'profileIn 0.5s ease 0.1s both' }}>
                  <SectionCard title="매칭 선호도" icon={<Favorite sx={{ fontSize: 20 }} />} accent={PINK}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                      {userDetail.preferredAge && (
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                            선호 나이대
                          </Typography>
                          <Chip
                            label={getPreferredAgeText(userDetail.preferredAge)}
                            sx={{ background: `${PINK}15`, color: PINK, border: `1px solid ${PINK}30`, fontWeight: 700, fontSize: '0.8rem' }}
                          />
                        </Box>
                      )}
                      {userDetail.preferredRegions && userDetail.preferredRegions.length > 0 && (
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                            선호 지역
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {userDetail.preferredRegions.map((region, i) => (
                              <Chip
                                key={i}
                                label={`${region.level1Name} ${region.level2Name}`}
                                size="small"
                                icon={<LocationOn sx={{ fontSize: '14px !important', color: '#1976D2 !important' }} />}
                                sx={{ background: '#E3F2FD', color: '#1976D2', border: '1px solid #BBDEFB', fontWeight: 600, fontSize: '0.78rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {userDetail.interests && userDetail.interests.length > 0 && (
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                            관심사
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {userDetail.interests.map((interest, i) => (
                              <Chip
                                key={i}
                                label={interest.name}
                                size="small"
                                sx={{ background: `${CORAL}15`, color: CORAL, border: `1px solid ${CORAL}30`, fontWeight: 600, fontSize: '0.78rem' }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </SectionCard>
                </Box>
              )}

              {/* 프로필 소개 */}
              {(userDetail.oneLiner || userDetail.selfIntro) && (
                <Box sx={{ animation: 'profileIn 0.5s ease 0.15s both' }}>
                  <SectionCard title="프로필 소개" icon={<Person sx={{ fontSize: 20 }} />} accent="#7B1FA2">
                    {userDetail.oneLiner && (
                      <Box sx={{ mb: userDetail.selfIntro ? 2.5 : 0 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                          한줄 소개
                        </Typography>
                        <Box sx={{
                          p: 2.5, borderRadius: '12px',
                          background: 'linear-gradient(135deg, #FAF5FF, #F3E8FF)',
                          borderLeft: '4px solid #7B1FA2',
                        }}>
                          <Typography sx={{ fontSize: '1.6rem', color: '#DDD', lineHeight: 1, mb: -1, fontFamily: 'Georgia, serif' }}>"</Typography>
                          <Typography sx={{ fontStyle: 'italic', color: '#4A1265', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.6, px: 1 }}>
                            {userDetail.oneLiner}
                          </Typography>
                          <Typography sx={{ fontSize: '1.6rem', color: '#DDD', lineHeight: 1, mt: -1, textAlign: 'right', fontFamily: 'Georgia, serif' }}>"</Typography>
                        </Box>
                      </Box>
                    )}
                    {userDetail.selfIntro && (
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#AAA', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
                          자기소개
                        </Typography>
                        <Box sx={{ p: 2.5, borderRadius: '12px', background: '#FAFAFA', border: '1px solid #EEE' }}>
                          <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, color: '#444', fontSize: '0.9rem' }}>
                            {userDetail.selfIntro}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </SectionCard>
                </Box>
              )}

              {/* 프로필 이미지 */}
              {sortedPhotos.length > 0 && (
                <Box sx={{ animation: 'profileIn 0.5s ease 0.2s both' }}>
                  <SectionCard
                    title={`프로필 이미지 (${sortedPhotos.length}장)`}
                    icon={<PhotoLibrary sx={{ fontSize: 20 }} />}
                    accent="#00897B"
                  >
                    <Box sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                      gap: 1.5,
                    }}>
                      {sortedPhotos.map((photo, idx) => (
                        <Box
                          key={photo.id}
                          onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                          sx={{
                            position: 'relative', borderRadius: '12px', overflow: 'hidden',
                            aspectRatio: '1', cursor: 'pointer',
                            border: `2px solid ${idx === 0 ? PINK : '#EEE'}`,
                            transition: 'all 0.25s ease',
                            '&:hover': { transform: 'scale(1.03)', boxShadow: '0 8px 24px rgba(0,0,0,0.15)', border: `2px solid ${PINK}` },
                            '&:hover .photo-overlay': { opacity: 1 },
                          }}
                        >
                          <Box component="img" src={photo.endpointUrl} alt={`프로필 ${idx + 1}`}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                          <Box className="photo-overlay" sx={{
                            position: 'absolute', inset: 0,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
                            opacity: 0, transition: 'opacity 0.25s ease',
                            display: 'flex', alignItems: 'flex-end', p: 1,
                          }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.72rem', fontWeight: 700 }}>#{idx + 1}</Typography>
                          </Box>
                          {idx === 0 && (
                            <Box sx={{
                              position: 'absolute', top: 6, left: 6,
                              background: PINK, color: '#fff',
                              px: 0.8, py: 0.2, borderRadius: '6px',
                              fontSize: '0.62rem', fontWeight: 800,
                            }}>
                              대표
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </SectionCard>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ── Lightbox ── */}
        {sortedPhotos.length > 0 && (
          <Dialog
            open={lightboxOpen}
            onClose={() => setLightboxOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { bgcolor: 'rgba(5,5,15,0.97)', borderRadius: '20px', overflow: 'hidden', maxHeight: '95vh' } }}
          >
            <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400 }}>
              <IconButton
                onClick={() => setLightboxOpen(false)}
                sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.1)', zIndex: 2, '&:hover': { background: 'rgba(255,255,255,0.2)' } }}
              >
                <Close />
              </IconButton>
              {sortedPhotos.length > 1 && (
                <IconButton
                  onClick={() => setLightboxIndex(p => p === 0 ? sortedPhotos.length - 1 : p - 1)}
                  sx={{ position: 'absolute', left: 12, color: '#fff', background: 'rgba(255,255,255,0.1)', zIndex: 2, '&:hover': { background: 'rgba(255,255,255,0.2)' } }}
                >
                  <ChevronLeft />
                </IconButton>
              )}
              <Box component="img" src={sortedPhotos[lightboxIndex]?.endpointUrl} alt={`프로필 ${lightboxIndex + 1}`}
                sx={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain', display: 'block', mx: 'auto', borderRadius: '8px' }} />
              {sortedPhotos.length > 1 && (
                <IconButton
                  onClick={() => setLightboxIndex(p => p === sortedPhotos.length - 1 ? 0 : p + 1)}
                  sx={{ position: 'absolute', right: 12, color: '#fff', background: 'rgba(255,255,255,0.1)', zIndex: 2, '&:hover': { background: 'rgba(255,255,255,0.2)' } }}
                >
                  <ChevronRight />
                </IconButton>
              )}
              <Box sx={{
                position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
                color: '#fff', px: 2, py: 0.5, borderRadius: '100px', fontSize: '0.8rem', fontWeight: 600,
              }}>
                {lightboxIndex + 1} / {sortedPhotos.length}
              </Box>
            </DialogContent>
          </Dialog>
        )}

        {/* ── Status change dialog ── */}
        <Dialog
          open={statusDialog}
          onClose={() => setStatusDialog(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden' } }}
        >
          <Box sx={{ background: `linear-gradient(135deg, #1A0A2E, #4A1265)`, p: 3, color: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: '10px',
                background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Edit />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>심사 상태 변경</Typography>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>
                  {userDetail.name}님의 심사 상태를 변경합니다
                </Typography>
              </Box>
            </Box>
          </Box>

          <DialogContent sx={{ p: 3, pt: 3 }}>
            <Box sx={{
              p: 2, borderRadius: '12px',
              background: reviewCfg.bg, border: `1px solid ${reviewCfg.dot}33`,
              display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5,
            }}>
              <Box sx={{ color: reviewCfg.color }}>{reviewCfg.icon}</Box>
              <Box>
                <Typography sx={{ fontSize: '0.7rem', color: '#AAA', fontWeight: 600 }}>현재 상태</Typography>
                <Typography sx={{ fontWeight: 700, color: reviewCfg.color }}>{reviewCfg.label}</Typography>
              </Box>
            </Box>

            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', mb: 0.8 }}>변경할 상태</Typography>
            <FormControl fullWidth sx={{ mb: 2.5 }}>
              <Select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as ReviewStatus)}
                sx={{ borderRadius: '12px', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PINK } }}
              >
                <MenuItem value={ReviewStatus.SUBMITTED}>제출됨</MenuItem>
                <MenuItem value={ReviewStatus.APPROVED}>승인됨</MenuItem>
                <MenuItem value={ReviewStatus.UPDATED}>수정됨</MenuItem>
                <MenuItem value={ReviewStatus.REJECTED}>거부됨</MenuItem>
              </Select>
            </FormControl>

            {newStatus === ReviewStatus.REJECTED && (
              <>
                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', mb: 0.8 }}>
                  거절 사유 코드 선택 (필수 · 1개 이상)
                </Typography>
                <Box sx={{ border: '1px solid #E0E0E0', borderRadius: '12px', p: 1.5, mb: 2.5 }}>
                  {REJECT_REASON_CODES.map(({ code, label }) => (
                    <FormControlLabel
                      key={code}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedRejectCodes.includes(code)}
                          onChange={(e) => {
                            setSelectedRejectCodes(prev =>
                              e.target.checked ? [...prev, code] : prev.filter(c => c !== code)
                            );
                          }}
                          sx={{ color: PINK, '&.Mui-checked': { color: PINK } }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '0.82rem' }}>{label}</Typography>}
                      sx={{ display: 'flex', mb: 0.5 }}
                    />
                  ))}
                </Box>
              </>
            )}

            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#888', mb: 0.8 }}>
              {newStatus === ReviewStatus.REJECTED ? '부연 설명 (선택)' : '변경 사유 (선택)'}
            </Typography>
            <TextField
              fullWidth multiline rows={3}
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder={newStatus === ReviewStatus.REJECTED ? '추가 안내 사항이 있으면 입력해주세요...' : '변경 사유를 입력해주세요...'}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', '&.Mui-focused fieldset': { borderColor: PINK } } }}
            />
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => { setStatusDialog(false); setStatusReason(''); setSelectedRejectCodes([]); }}
              sx={{ borderRadius: '10px', textTransform: 'none', color: '#888' }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleStatusChange}
              disabled={
                newStatus === userDetail.reviewStatus ||
                (newStatus === ReviewStatus.REJECTED && selectedRejectCodes.length === 0)
              }
              sx={{
                borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3,
                background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
                boxShadow: `0 4px 14px ${PINK}44`,
                '&:hover': { background: `linear-gradient(135deg, #D63384, #E55A28)` },
                '&:disabled': { background: '#EEE', color: '#BBB', boxShadow: 'none' },
              }}
            >
              상태 변경
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};

export default AdminUserDetailPage;
